'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageWhyPropTradingSchema } from '@/sanity/pages/PageWhyPropTradingTemplate';
import { GroqImage } from '@/helpers/Image';
import { CTAButton } from '@/shadcn/ui/button';

const ease = [0.25, 0.1, 0.25, 1] as const;

const DotIcon = () => (
  <div className="flex-shrink-0 flex items-center justify-center w-[3.875rem] h-[3.875rem] mt-[0.125rem]">
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="4"
        y="4"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="8"
        y="8"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="12"
        y="4"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="8"
        y="4"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="8"
        y="12"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="12"
        y="8"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="12"
        y="12"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="12"
        y="16"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="8"
        y="16"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="4"
        y="20"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="8"
        y="20"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="16"
        y="8"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="16"
        y="12"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="16"
        y="16"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
      <rect
        x="20"
        y="12"
        width="2"
        height="2"
        rx="0.4"
        fill="rgba(97,91,80,0.35)"
      />
    </svg>
  </div>
);

const UserIcon = () => (
  <div className="flex-shrink-0 bg-[#003828] flex items-center justify-center p-[1rem] mt-[0.125rem]">
    <svg
      viewBox="0 0 9.375 13.125"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[1.875rem] h-[1.875rem]"
    >
      <path
        d="M4.6875 0.9375C5.15105 0.9375 5.60419 1.07496 5.98962 1.33249C6.37505 1.59003 6.67545 1.95607 6.85284 2.38434C7.03024 2.8126 7.07665 3.28385 6.98622 3.73849C6.89578 4.19314 6.67256 4.61075 6.34478 4.93853C6.017 5.26631 5.59939 5.48953 5.14474 5.57997C4.6901 5.6704 4.21885 5.62399 3.79059 5.44659C3.36232 5.2692 2.99628 4.9688 2.73874 4.58337C2.48121 4.19794 2.34375 3.7448 2.34375 3.28125C2.34375 2.65965 2.59068 2.06351 3.03022 1.62397C3.46976 1.18443 4.0659 0.9375 4.6875 0.9375ZM4.6875 0C4.03853 0 3.40413 0.192442 2.86454 0.55299C2.32494 0.913539 1.90437 1.426 1.65602 2.02557C1.40767 2.62514 1.34269 3.28489 1.4693 3.92139C1.59591 4.55789 1.90842 5.14255 2.36731 5.60144C2.8262 6.06034 3.41086 6.37284 4.04736 6.49945C4.68386 6.62606 5.34361 6.56108 5.94318 6.31273C6.54275 6.06438 7.05521 5.64381 7.41576 5.10421C7.77631 4.56462 7.96875 3.93022 7.96875 3.28125C7.96875 2.41101 7.62305 1.57641 7.00769 0.961056C6.39234 0.345702 5.55774 0 4.6875 0Z"
        fill="white"
      />
      <path
        d="M9.375 13.125H8.4375V10.7812C8.4375 10.4735 8.37688 10.1687 8.25909 9.88434C8.14131 9.59998 7.96867 9.34161 7.75103 9.12397C7.53339 8.90633 7.27502 8.73369 6.99066 8.61591C6.70631 8.49812 6.40154 8.4375 6.09375 8.4375H3.28125C2.65965 8.4375 2.06351 8.68443 1.62397 9.12397C1.18443 9.56351 0.9375 10.1596 0.9375 10.7812V13.125H0V10.7812C0 9.91101 0.345702 9.07641 0.961056 8.46106C1.57641 7.8457 2.41101 7.5 3.28125 7.5H6.09375C6.96399 7.5 7.79859 7.8457 8.41394 8.46106C9.0293 9.07641 9.375 9.91101 9.375 10.7812V13.125Z"
        fill="white"
      />
    </svg>
  </div>
);

const UsersIcon = () => (
  <div className="flex-shrink-0 bg-[#003828] flex items-center justify-center p-[1rem] mt-[0.125rem]">
    <svg
      viewBox="0 0 13.125 13.125"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[1.875rem] h-[1.875rem]"
    >
      <path
        d="M13.125 13.125H12.1875V10.7812C12.1868 10.1599 11.9396 9.56414 11.5003 9.12475C11.0609 8.68536 10.4651 8.43821 9.84375 8.4375V7.5C10.7137 7.50097 11.5477 7.84698 12.1629 8.46212C12.778 9.07727 13.124 9.91131 13.125 10.7812V13.125Z"
        fill="white"
      />
      <path
        d="M9.375 13.125H8.4375V10.7812C8.43677 10.1599 8.1896 9.56416 7.75022 9.12478C7.31084 8.6854 6.71513 8.43823 6.09375 8.4375H3.28125C2.65987 8.43823 2.06416 8.6854 1.62478 9.12478C1.1854 9.56416 0.938232 10.1599 0.9375 10.7812V13.125H0V10.7812C0.00101727 9.91132 0.347046 9.07731 0.962179 8.46218C1.57731 7.84705 2.41132 7.50102 3.28125 7.5H6.09375C6.96368 7.50102 7.79769 7.84705 8.41282 8.46218C9.02795 9.07731 9.37398 9.91132 9.375 10.7812V13.125Z"
        fill="white"
      />
      <path
        d="M8.4375 0V0.9375C9.0591 0.9375 9.65524 1.18443 10.0948 1.62397C10.5343 2.06351 10.7812 2.65965 10.7812 3.28125C10.7812 3.90285 10.5343 4.49899 10.0948 4.93853C9.65524 5.37807 9.0591 5.625 8.4375 5.625V6.5625C9.30774 6.5625 10.1423 6.2168 10.7577 5.60144C11.373 4.98609 11.7188 4.15149 11.7188 3.28125C11.7188 2.41101 11.373 1.57641 10.7577 0.961056C10.1423 0.345702 9.30774 0 8.4375 0Z"
        fill="white"
      />
      <path
        d="M4.6875 0.9375C5.15105 0.9375 5.60419 1.07496 5.98962 1.33249C6.37505 1.59003 6.67545 1.95607 6.85284 2.38434C7.03024 2.8126 7.07665 3.28385 6.98622 3.73849C6.89578 4.19314 6.67256 4.61075 6.34478 4.93853C6.017 5.26631 5.59939 5.48953 5.14474 5.57997C4.6901 5.6704 4.21885 5.62399 3.79059 5.44659C3.36232 5.2692 2.99628 4.9688 2.73874 4.58337C2.48121 4.19794 2.34375 3.7448 2.34375 3.28125C2.34375 2.65965 2.59068 2.06351 3.03022 1.62397C3.46976 1.18443 4.0659 0.9375 4.6875 0.9375ZM4.6875 0C4.03853 0 3.40413 0.192442 2.86454 0.55299C2.32494 0.913539 1.90437 1.426 1.65602 2.02557C1.40767 2.62514 1.34269 3.28489 1.4693 3.92139C1.59591 4.55789 1.90841 5.14255 2.36731 5.60144C2.8262 6.06034 3.41086 6.37284 4.04736 6.49945C4.68386 6.62606 5.34361 6.56108 5.94318 6.31273C6.54275 6.06438 7.05521 5.64381 7.41576 5.10421C7.77631 4.56462 7.96875 3.93022 7.96875 3.28125C7.96875 2.41101 7.62305 1.57641 7.00769 0.961056C6.39234 0.345702 5.55774 0 4.6875 0Z"
        fill="white"
      />
    </svg>
  </div>
);

const iconMap = {
  dot: DotIcon,
  user: UserIcon,
  users: UsersIcon,
};

export default function WhyChallengeSection({
  data,
}: {
  data: PageWhyPropTradingSchema;
}) {
  if (!data?.whyChallengeTitle && !data?.whyChallengeItems?.length) return null;

  return (
    <section className="relative w-full flex flex-col items-center bg-white py-[8rem] lg:pb-[15rem] lg:pt-[10rem] px-[2rem] lg:px-[6rem]">
      <div className="w-[min(180rem,100%)] flex flex-col items-center gap-[6rem] lg:gap-[8rem]">
        {/* Title — centered */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1rem] text-center"
        >
          {data?.whyChallengeChip && (
            <p className="mb-0 font-blacker italic text-[2rem] text-black/70 leading-[1.5]">
              {data.whyChallengeChip}
            </p>
          )}
          {data?.whyChallengeTitle && (
            <div className="richtext prose-h2:text-[4rem] lg:prose-h2:text-[6rem] prose-h2:font-medium prose-h2:leading-[1.15] prose-h2:tracking-[-0.3rem] prose-em:text-[#003828] max-w-[75rem]">
              <PortableText
                value={data.whyChallengeTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {/* Two-column */}
        <div className="flex flex-col items-stretch lg:flex-row gap-[6rem] lg:gap-[13rem]">
          {/* Left — items */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col gap-[4rem] lg:w-[64rem] "
          >
            {!!data?.whyChallengeItems?.length && (
              <ul className="flex flex-col gap-[4rem] justify-between h-full list-none p-0 m-0">
                {data.whyChallengeItems.map((item, i) => {
                  if (!item) return null;
                  const Icon = iconMap[item.icon ?? 'dot'] ?? DotIcon;
                  return (
                    <li
                      key={item._key ?? i}
                      className="flex gap-[1.5rem] items-start"
                    >
                      <Icon />
                      {item.text && (
                        <div className="richtext richtext-start prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:tracking-[-0.1125rem] prose-p:text-black/80">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>

          {/* Right — image */}
          {data?.whyChallengeImage && (
            <motion.div
              initial={{ opacity: 0, x: 24, scale: 0.98 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease, delay: 0.12 }}
              className="w-full lg:h-[58.25rem] lg:w-[64.625rem] overflow-hidden"
            >
              <GroqImage
                src={data.whyChallengeImage}
                alt={data.whyChallengeImage.name || ''}
                className="w-full h-full object-cover"
              />
            </motion.div>
          )}
        </div>

        {/* CTA — centered */}
        {data?.whyChallengeCTA && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, ease }}
            className="flex justify-center"
          >
            <CTAButton data={data.whyChallengeCTA} />
          </motion.div>
        )}
      </div>
    </section>
  );
}
